/**
 * Auth Service
 *
 * Plain async functions extracted from useAuthModal and useAuth.
 * Used by SessionManager to interact with the auth store imperatively.
 * Does NOT replace useAuth — consumers continue using the hook.
 */

import { authAPI, type VerifyRequest } from "@/api/auth";
import { useAuthStore } from "@/store/auth-store";
import bs58 from "bs58";

interface PersistedAuth {
  isAuthenticated: boolean;
  wallet: string | null;
  token: string | null;
  expiresAt: number | null;
  jti: string | null;
  sid: string | null;
}

/**
 * Read JWT state from the rehydrated auth store.
 */
export function getPersistedAuth(): PersistedAuth {
  const state = useAuthStore.getState();
  return {
    isAuthenticated: state.isAuthenticated,
    wallet: state.wallet,
    token: state.token,
    expiresAt: state.expiresAt,
    jti: state.jti,
    sid: state.sid,
  };
}

/**
 * Check if the persisted token is expired.
 */
export function isTokenExpired(): boolean {
  return useAuthStore.getState().isTokenExpired();
}

/**
 * Request an authentication challenge from the backend.
 * Extracted from useAuthModal.requestChallenge (~line 77).
 */
export async function getChallenge(
  address: string,
): Promise<{ message: string; blockhash?: string }> {
  const response = await authAPI.getChallenge(address);
  return {
    message: response.message,
    blockhash: response.blockhash,
  };
}

/**
 * Type guard to check if the signature result is a transaction-based signature.
 */
function isTxSignatureResult(
  result: Uint8Array | { serializedTx: string; blockhash: string },
): result is { serializedTx: string; blockhash: string } {
  return (
    typeof result === "object" &&
    "serializedTx" in result &&
    "blockhash" in result
  );
}

/**
 * Verify a signature with the backend and return auth credentials.
 * Extracted from useAuthModal.confirmSign (~line 226).
 */
export async function verifySignature(
  walletAddress: string,
  message: string,
  signResult: Uint8Array | { serializedTx: string; blockhash: string },
): Promise<{
  wallet: string;
  token: string;
  jti: string;
  sid: string;
  expiresIn: number;
}> {
  let verifyRequest: VerifyRequest;

  if (isTxSignatureResult(signResult)) {
    verifyRequest = {
      wallet: walletAddress,
      message,
      signedTransaction: signResult.serializedTx,
    };
  } else {
    const signatureBase58 = bs58.encode(signResult);
    verifyRequest = {
      wallet: walletAddress,
      message,
      signature: signatureBase58,
    };
  }

  const response = await authAPI.verify(verifyRequest);
  const expiresIn = parseInt(response.expiresIn.replace("s", ""), 10);

  return {
    wallet: response.wallet,
    token: response.token,
    jti: response.jti,
    sid: response.sid,
    expiresIn,
  };
}

/**
 * Set authenticated state in the auth store.
 */
export function setAuthenticated(
  wallet: string,
  token: string,
  jti: string,
  sid: string,
  expiresIn: number,
): void {
  useAuthStore.getState().setAuthenticated(wallet, token, jti, sid, expiresIn);
}

/**
 * Clear auth state.
 */
export function clearAuth(): void {
  useAuthStore.getState().clearAuth();
}

/**
 * Logout on the server (invalidate JWT).
 * Extracted from useAuth.logout (~line 150).
 */
export async function logoutServer(token: string): Promise<void> {
  try {
    await authAPI.logout(token);
  } catch (err) {
    console.warn("Logout API call failed:", err);
  }
}

/**
 * Get the current auth token.
 */
export function getToken(): string | null {
  return useAuthStore.getState().token;
}

/**
 * Get the expiry timestamp.
 */
export function getExpiresAt(): number | null {
  return useAuthStore.getState().expiresAt;
}

/**
 * Subscribe to auth store changes.
 * Returns an unsubscribe function.
 */
export function subscribeToAuthChanges(
  callback: (isAuthenticated: boolean, wasAuthenticated: boolean) => void,
): () => void {
  let prev = useAuthStore.getState().isAuthenticated;
  return useAuthStore.subscribe((state) => {
    const current = state.isAuthenticated;
    if (current !== prev) {
      callback(current, prev);
      prev = current;
    }
  });
}
